import React from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import FormValidator from './FormValidator'
import MaterialInputable from './MaterialInputable'
import MaterialZipcode from './MaterialZipcode'
import StateSelection from './StateSelection'

const layouts = {
	horizontal: 'horizontal',
	vertical: 'vertical',
	table: 'table'
}

class Address extends React.Component {
	/**
	 * Performs validation of the data in the dialog.
	 * @param {props} props - the properties passed to the component
	 * @returns {void} - no return value
	 * @public
	 */
	constructor(props) {
		super(props)

		this.onAddressChange = this.onAddressChange.bind(this)

		this.validator = this.getValidator(true, props.names, props.values[props.names.country])

		this.state = {
			validation: this.validator.valid(),
		}
	}

	/**
	 * Performs validation of the data in the dialog.
	 * @param {event} event - the calling event
	 * @returns {void} - no return value
	 * @public
	 */
	onAddressChange(event) {
		const key = global.utilities.getKeyByValue(this.props.names, event.target.name)

		if( this.state.validation.hasOwnProperty(key)) {
			let validation = Object.assign({}, this.state.validation)
			validation[key].isInvalid = false
			validation[key].message = ''

			this.setState({ validation: validation })
		}

		switch( key ) {
			case 'state':
				if( this.props.onSelectChange )
					this.props.onSelectChange(event)
				break

			default:
				if (this.props.onChange)
					this.props.onChange(event)
		}
	}

	/**
	 * Performs validation of the data in the dialog.
	 * @returns {boolean} - this the current object valid
	 * @public
	 */
	async isValid() {
		const validation = await this.validator.validate(this.state, this.props)
		this.setState({ validation })
		
		return validation.isValid
	}

	/**
	 * Performs validation of the data in the dialog.
	 * @param {layouts} layout - the desired layout of the address
	 * @returns {object} - the selected sizing object
	 * @public
	 */
	getSize(layout, extendedAddress) {
		const sizes = {
			horizontal: {
				addressLine1: { xs: 12, md: 5 },
				city: { xs: 6, md: 3 },
				state: { xs: 3, md: 2 },
				zip: { xs: 3, md: 2 }
			},
			extendedAddress: {
				addressLine1: { xs: 12, md: 4 },
				addressLine2: { xs: 12, md: 4 },
				city: { xs: 6, md: 2 },
				state: { xs: 3, md: 1 },
				zip: { xs: 3, md: 1 }
			},
			table: {
				addressLine1: { xs: 12 },
				addressLine2: { xs: 12 },
				city: { xs: 6 },
				state: { xs: 3 },
				zip: { xs: 3 }
			}
		}

		switch (layout) {
			case layouts.horizontal:
				if (extendedAddress) {
					return sizes.extendedAddress
				}
				return sizes.horizontal

			default:
				return sizes.table
		}
	}

	/**
	 * gets the current formValidator
	 * @param {boolean} disabled - is this address disabled/not required?
	 * @param {Array} names - the array of the current field names
	 * @param {string} country - the currently selected country code
	 * @returns {object} - the selected sizing object
	 * @public
	 */
	getValidator(disabled, names, country) {
		return new FormValidator([
			{
				prop: {
					object: 'values',
					name: names.addressLine1
				},
				field: 'addressLine1',
				method: 'isEmpty',
				disabled: disabled,
				validWhen: false,
				message: 'Address is required'
			},
			{
				prop: {
					object: 'values',
					name: names.city
				},
				field: 'city',
				method: 'isEmpty',
				disabled: disabled,
				validWhen: false,
				message: 'City is required'
			},
			{
				prop: {
					object: 'values',
					name: names.state
				},
				field: 'state',
				method: 'isEmpty',
				disabled: disabled,
				validWhen: false,
				message: 'State is required'
			},
			{
				prop: {
					object: 'values',
					name: names.zip
				},
				field: 'zip',
				method: 'isEmpty',
				disabled: disabled,
				validWhen: false,
				message: country === 'CAN' ? 'Postal Code is required' : 'Zipcode is required'
			},
			{
				prop: {
					object: 'values',
					name: names.zip
				},
				field: 'zip',
				method: 'isPostalCode',
				disabled: disabled,
				args: [country === 'CAN' ? 'CA' : 'US'],
				validWhen: true,
				message: country === 'CAN' ? 'Postal Code is invalid' : 'Zipcode must be 5 or 9 digits'
			},
		])
	}

	/**
	 * Performs validation of the data in the dialog.
	 * @returns {Component} - returns the formatted objet
	 * @public
	 */
	render() {
		const { names, values, extendedAddress, onBlur, layout, autoFocus, autoUpdate, disabled, required } = this.props
		const { validation } = this.state

		const displayExtendedAddress = extendedAddress && (!disabled || (disabled && ((values[names.addressLine2] !== null) && (values[names.addressLine2] !== ''))))

		let size = this.getSize(layout, displayExtendedAddress)

		this.validator = this.getValidator(!required || disabled, names, values[names.country])

		return (
			<Grid container spacing={1}>
				<Grid item {...size.addressLine1}> 
					<MaterialInputable
						name={names.addressLine1}
						label='Street Address'
						value={values ? values[names.addressLine1] : ''}
						autoFocus={autoFocus}
						autoUpdate={autoUpdate}
						required={required}
						disabled={disabled}
						error={validation.addressLine1.isInvalid}
						errorMessage={validation.addressLine1.message}
						onBlur={onBlur}
						onChange={this.onAddressChange} />
				</Grid>
				{displayExtendedAddress &&
					<Grid item {...size.addressLine2}>
						<MaterialInputable
							name={names.addressLine2}
							label='Street Address 2'
							value={values ? (values[names.addressLine2] === null ? '' : values[names.addressLine2]) : ''}
							autoUpdate={autoUpdate}
							disabled={disabled}
							onBlur={onBlur}
							onChange={this.onAddressChange} />
					</Grid>
				}
				<Grid item {...size.city}>
					<MaterialInputable
						name={names.city}
						label='City'
						value={values ? values[names.city] : ''}
						autoUpdate={autoUpdate}
						required={required}
						disabled={disabled}
						error={validation.city.isInvalid}
						errorMessage={validation.city.message}
						onBlur={onBlur}
						onChange={this.onAddressChange} />
				</Grid>
				<Grid item {...size.state}>
					<StateSelection
						name={names.state}
						label={values[names.country] === 'CAN' ? 'Province' : 'State'}
						value={values ? values[names.state] : ''}
						required={required}
						disabled={disabled}
						error={validation.state.isInvalid}
						errorMessage={validation.state.message}
						country={values ? values[names.country] : 'USA'}
						onBlur={onBlur}
						onChange={this.onAddressChange} />
				</Grid>
				<Grid item {...size.zip}>
					<MaterialZipcode
						name={names.zip}
						country={values[names.country]}
						label={values[names.country] === 'CAN' ? 'Postal Code' : 'Zipcode'}
						value={values ? values[names.zip] : ''}
						autoUpdate={autoUpdate}
						required={required}
						disabled={disabled}
						error={validation.zip.inInvalid}
						errorMessage={validation.zip.message}
						onBlur={onBlur}
						onChange={this.onAddressChange} />
				</Grid>
			</Grid>
		)
	}
}

Address.propTypes = {
	classes: PropTypes.object,
	names: PropTypes.object,
	values: PropTypes.object.isRequired,
	extendedAddress: PropTypes.bool,
	autoFocus: PropTypes.bool,
	autoUpdate: PropTypes.bool,
	required: PropTypes.bool,
	disabled: PropTypes.bool,
	onBlur: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	onSelectChange: PropTypes.func.isRequired,
	layout: PropTypes.oneOf(Object.keys(layouts))
}

Address.defaultProps = {
	names: {
		addressLine1: 'address',
		addressLine2: 'address2',
		city: 'city',
		state: 'state',
		zip: 'zip',
		country: 'countryId'
	},
	extendedAddress: false,
	autoFocus: false,
	autoUpdate: false,
	required: false,
	disabled: false,
	layout: 'table'
}

Address.layouts = layouts

export default Address


// WEBPACK FOOTER //
// ./src/components/Address.js